import { useState } from 'react';
import type { VinBroadcastCandidate } from '../../lib/decode/vinDetection';

type VinDetectionCardProps = {
  candidates: VinBroadcastCandidate[];
};

function formatMs(value: number) {
  return `${value.toFixed(3)} ms`;
}

export function VinDetectionCard({ candidates }: VinDetectionCardProps) {
  const [previewOpen, setPreviewOpen] = useState(false);

  if (candidates.length === 0) return null;

  const primary = candidates[0];

  return (
    <section className="section vin-detection-section" aria-label="Possible VIN-like pattern">
      <article className="data-panel vin-detection-card">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Pattern detection</p>
            <h2>Possible VIN-like pattern detected (needs verification)</h2>
            <p className="panel-note">
              SmartCAN found a 3-part ASCII sequence. The preview is redacted for privacy.
            </p>
          </div>
          <span className="vin-detection-badge">Needs review</span>
        </div>

        <div className="vin-detection-grid">
          <div>
            <small>CAN ID</small>
            <strong>{primary.canIdHex}</strong>
          </div>
          <div>
            <small>Frames</small>
            <strong>3-part sequence</strong>
          </div>
          <div>
            <small>Observed</small>
            <strong>{primary.sequenceCount.toLocaleString()}x</strong>
          </div>
          <div>
            <small>First / last seen</small>
            <strong>{formatMs(primary.firstSeenMs)} / {formatMs(primary.lastSeenMs)}</strong>
          </div>
        </div>

        <div className="vin-preview-row">
          <button className="button secondary" type="button" onClick={() => setPreviewOpen((value) => !value)}>
            {previewOpen ? 'Hide decoded VIN preview' : 'Preview decoded VIN (redacted)'}
          </button>
          {previewOpen && <code className="vin-redacted-preview">{primary.preview}</code>}
        </div>

        <p className="panel-note">
          Detection rule: byte 0 cycles 0, 1, 2 and bytes 1-7 decode as printable ASCII. Padding is stripped before a 17-character VIN-like value is checked.
        </p>
        <p className="warning-text">
          This is a heuristic detection based on ASCII patterns and frame sequencing. Verify against known VIN before trusting.
        </p>
      </article>
    </section>
  );
}
